import type { Styles } from '@adornis/ass/style.js';
import { A } from '@adornis/base/env-info.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { XContainerExtension } from '@adornis/buildify/client/extensions/ContainerExtension.js';
import { Size } from '@adornis/buildify/client/globals/enums.js';
import { __renderBase } from '@adornis/buildify/client/globals/methods.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import type { Base } from '@adornis/buildify/db/Base.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { XGrid } from '@adornis/chemistry/elements/components/x-grid.js';
import { type XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import logo_hexa from '@adornis/digitale-helden-shared/assets/dhg-hexa-herz.svg';
import '@adornis/digitale-helden-shared/client/theme/d-accordeon.js';
import '@adornis/digitale-helden-shared/client/theme/d-button.js';
import '@adornis/digitale-helden-shared/client/theme/d-icon.js';
import { DigitaleHeldenFont } from '@adornis/digitale-helden-shared/client/theme/fonts';
import '@adornis/file-utils/client/x-image.js';
import { signalToObservable } from '@adornis/functional-lit/signal-to-observable.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { distinctUntilChanged, map } from 'rxjs';
import '../../client/theme/d-text.js';
import { BaseTopbar, TopbarExtensionItem } from '../db/BaseTopbar.js';
import './marketing-topbar-dialog.js';

export const MarketingTopbarExtension: BuildifyExtension<BaseTopbar> = {
  class: BaseTopbar,
  toolbar: {
    icon: 'compass',
    text: {
      default: 'Nav',
    },
  },
  group: 'Marketing',
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <marketing-topbar-extension
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></marketing-topbar-extension>
    `;
  },
  editor({ content, contentController, controllerBaseKeyPath, host }) {
    return html`
      <d-flex space="sm">
        ${repeat(
          content.items ?? [],
          item => item._id,
          (item, index) => html`
            <x-flex space="sm">
              <x-flex horizontal crossaxis-center space-between>
                <x-text> <b>${index + 1}</b>. Navigationsitem </x-text>
                <x-icon-button
                  icon="delete"
                  background=${host.colors.tone.error}
                  color="#fff"
                  @click=${() => {
                    content.items.splice(index, 1);
                    host.requestUpdate();
                  }}
                ></x-icon-button>
              </x-flex>
              <x-input
                placeholder="Name"
                ${contentController.field(...controllerBaseKeyPath, 'items', index, 'name')}
              ></x-input>
              <x-input
                placeholder="Pfad"
                ${contentController.field(...controllerBaseKeyPath, 'items', index, 'path')}
              ></x-input>
              <x-hr></x-hr>
            </x-flex>
          `,
        )}
        <x-icon-button
          ${css({ alignSelf: 'end' })}
          icon="add"
          background="#bbbbb4"
          color="#333"
          tooltip="weiteres Item hinzufügen"
          @click=${() => {
            if (!content.items) content.items = [];
            content.items.push(new TopbarExtensionItem({}));
            host.requestUpdate();
          }}
        >
        </x-icon-button>
      </d-flex>
    `;
  },
};

@customElement('marketing-topbar-extension')
export class MTopbarExtension extends XContainerExtension<BaseTopbar> {
  @state() private readonly _dropdownDialogElement = createRef<XNativeDialog<any>>();
  @state() private readonly _topbarElement = createRef<XGrid>();
  @state() private readonly _windowSize = new WindowSizeController(this);

  // topbar settings
  @state() private _viewItemInDropdown: Maybe<string>;
  @state() private _isMobileTopbarOpen: boolean = false;

  override connectedCallback(): void {
    super.connectedCallback();

    signalToObservable(routingStateSignal)
      .pipe(
        map(routingState => routingState.path),
        distinctUntilChanged(),
      )
      .subscribe(path => {
        this._dropdownDialogElement.value?.close(null);
      });
  }

  protected override willUpdate(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.willUpdate(_changedProperties);

    const dialog = this._dropdownDialogElement.value;
    if (!dialog) return;

    if (this.shouldMegaMenuBeOpened()) {
      // sollte geöffnet sein
      if (dialog.isOpen) return;
      dialog.open();
      return;
    }

    // sollte geschlossen sein
    if (!dialog.isOpen) return;
    dialog.close(null);
  }

  protected shouldMegaMenuBeOpened() {
    if (this._viewItemInDropdown && this._consumedSize.value === Size.DESKTOP) return true;
    if (
      (this._consumedSize.value === Size.TABLET || this._consumedSize.value === Size.MOBILE) &&
      this._isMobileTopbarOpen
    )
      return true;
    return false;
  }

  protected override _content(content: BaseTopbar) {
    return html`
      ${this._topbar(content)}
      ${this.isEditMode
        ? this._viewItemInDropdown
          ? this._dropdownMenuEditor(content, this._getSelectedItemContent())
          : nothing
        : this._dropdownMenu(content)}
    `;
  }

  protected _getSelectedItemContent() {
    const item = this.content.value?.items.find(item => item._id === this._viewItemInDropdown);
    if (!item) return null;
    return this._consumedContent.value?.find(c => c.parentID === this.content.value?._id && c.subItemID === item._id);
  }

  protected _topbar(content: BaseTopbar, isCopyForDialog: boolean = false) {
    const styling = {
      padding: '28px 32px',
      borderBottom: `2px solid ${this.colors.tone.neutralBase}`,
      background: '#fff',
      zIndex: '9999',
      position: 'relative',
      // 42px for container itself + padding top & padding bottom
      maxHeight: 'calc(42px + 2*28px)',
    };
    if (this._consumedSize.value === Size.DESKTOP) {
      // desktop topbar
      return html`
        <d-grid ${ref(isCopyForDialog ? undefined : this._topbarElement)} columns="200px 1fr 200px" ${css(styling)}>
          <!-- logo -->
          ${this._logo()}
          <!-- links -->
          ${this._links(content)}
          <!-- campus button -->
          ${this._campusButton()}
        </d-grid>
      `;
    }

    // mobile & tablet topbar
    return html`
      <d-flex
        ${ref(isCopyForDialog ? undefined : this._topbarElement)}
        ${css(styling)}
        horizontal
        crossaxis-center
        space-between
      >
        <!-- logo -->
        ${this._logo()}
        <!-- toggle -->
        <d-icon
          @click=${() => {
            this._isMobileTopbarOpen = !this.shouldMegaMenuBeOpened();
          }}
          ${css({
            cursor: 'pointer',
            userSelect: 'none',
            fontSize: this._isMobileTopbarOpen ? '32px' : '48px',
            color: this._isMobileTopbarOpen ? this.colors.tone.secondaryText : this.colors.tone.black,
          })}
        >
          ${this.shouldMegaMenuBeOpened() ? 'xmark' : 'bars'}
        </d-icon>
      </d-flex>
    `;
  }

  protected _campusButton() {
    return html`
      <d-button
        mode="outline"
        href=${'https://campus.digitale-helden.de/campus'}
        @focus=${e => {
          e.target.blur();
        }}
      >
        Campus
      </d-button>
    `;
  }

  protected _logo() {
    return html`
      <d-flex
        horizontal
        crossaxis-center
        space="sm"
        ${css({ cursor: 'pointer' })}
        @mouseenter=${() => {
          this.closeMenu();
        }}
        @click=${() => {
          this.closeMenu();
          goTo('/');
        }}
      >
        <img
          alt="Digitale Helden Logo"
          src=${A.absoluteUrl(logo_hexa)}
          ${css({ height: '40px', aspectRatio: '1 / 1' })}
        />
        <d-text large font-black color-grey> Digitale Helden </d-text>
      </d-flex>
    `;
  }

  protected _links(content: BaseTopbar) {
    return html`
      <d-flex horizontal crossaxis-center space="lg" w100 center>
        ${repeat(
          content.items,
          item => item._id,
          item => {
            const highlightLink = this._viewItemInDropdown
              ? this._viewItemInDropdown === item._id
              : window.location.pathname.startsWith(item.path);
            return html`
              <d-text
                class=${highlightLink ? 'highlight-link' : ''}
                @click=${() => {
                  this._viewItemInDropdown = item._id;
                }}
                @mouseenter=${e => {
                  this._viewItemInDropdown = item._id;
                }}
                ${css({
                  position: 'relative',
                  overflow: 'visible',
                  fontFamily: DigitaleHeldenFont.REGULAR,
                  textDecoration: 'none',
                  userSelect: 'none',
                  fontSize: '18px',
                  height: '18px',
                  cursor: 'pointer',
                })}
              >
                ${item.name}
              </d-text>
            `;
          },
        )}
      </d-flex>
    `;
  }

  protected _dropdownMenu(content: BaseTopbar) {
    //     if (this.isEditMode) return nothing;
    const item = content.items.find(i => i._id === this._viewItemInDropdown);
    const itemContent = this._contentFilteredSorted?.find(base => base.subItemID === item?._id);

    return html`
      <!-- backdrop -->
      <marketing-topbar-dialog
        .widthOfNav=${this._topbarElement.value?.clientWidth}
        ${ref(this._dropdownDialogElement)}
        @close=${() => {
          this.closeMenu();
        }}
      >
        <d-flex ${css({ position: 'relative', background: 'transparent' })} w100 h100>
          ${this._topbar(content, true)}

          <!-- mega menu -->
          <d-flex
            ${css({
              position: 'relative',
              background: '#fff',
              zIndex: '20',
              boxShadow: '0px 16px 16px rgba(0, 0, 0, 0.1)',
            })}
            w100
          >
            <!-- dialog content -->
            ${this._dialogContent(itemContent)}
          </d-flex>

          <!-- backdrop -->
          <d-flex
            @mouseenter=${() => {
              this.closeMenu();
            }}
            @click=${() => {
              this.closeMenu();
            }}
            ${css({
              position: 'absolute',
              left: '0',
              right: '0',
              top: '0',
              bottom: '0',
              zIndex: '10',
              backdropFilter: 'blur(8px)',
            })}
          >
            backgrop
          </d-flex>
        </d-flex>
      </marketing-topbar-dialog>
    `;
  }

  protected _dialogContent(itemContent: Maybe<Base>) {
    switch (this._consumedSize.value) {
      case Size.TABLET:
      case Size.MOBILE:
        return html`
          <d-flex ${css({ padding: '32px', gap: '40px' })}>
            ${repeat(
              this.content.value?.items ?? [],
              item => item._id,
              item => {
                const itemContent = this._contentFilteredSorted?.find(c => c.subItemID === item._id);
                const selected = this._viewItemInDropdown === item._id ? 0 : -1;
                return html`
                  <d-accordeon
                    ?highlight=${selected !== -1}
                    @selected-changed=${(e: ValueEvent<number>) => {
                      if (e.detail.value === -1) this._viewItemInDropdown = null;
                      else this._viewItemInDropdown = item._id;
                    }}
                    .selected=${selected}
                    .items=${[
                      {
                        title: html` <d-text> ${item.name} </d-text> `,
                        text: () => {
                          return this._renderItemContent(itemContent);
                        },
                      },
                    ]}
                  >
                  </d-accordeon>
                `;
              },
            )}
            ${this._campusButton()}
          </d-flex>
        `;
      case Size.DESKTOP:
        return this._renderItemContent(itemContent);
      default:
    }
  }

  protected _dropdownMenuEditor(content: BaseTopbar, itemContent: Maybe<Base>) {
    const item = content.items.find(i => i._id === this._viewItemInDropdown);
    if (!item) return nothing;

    return html`
      <d-flex
        space="md"
        ${css({
          cursor: 'pointer',
          position: 'relative',
          background: '#fff',
          zIndex: '20',
          boxShadow: '0px 16px 16px rgba(0, 0, 0, 0.1)',
          padding: this.spacing.md,
        })}
        w100
      >
        <!-- close -->
        <d-text
          ${css({ textAlign: 'center' })}
          @click=${() => {
            this.closeMenu();
          }}
        >
          schließen
        </d-text>

        <!-- content -->
        ${itemContent
          ? this._renderItemContent(itemContent)
          : html`
              <x-buildify-dropzone
                .index=${0}
                .parentID=${this.content.value?._id}
                .subItemID=${item._id}
              ></x-buildify-dropzone>
            `}
      </d-flex>
    `;
  }

  protected closeMenu() {
    this._isMobileTopbarOpen = false;
    this._viewItemInDropdown = null;
  }

  protected _renderItemContent(itemContent: Maybe<Base>) {
    if (!itemContent) return nothing;
    return __renderBase(this._consumedExtensions.value, itemContent);
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          marginBottom: '0px',
        },
        '.highlight-link': {
          position: 'relative',
          color: this.colors.accent,
        },
        '.highlight-link::after': {
          content: "''",
          position: 'absolute',
          bottom: '-8px',
          height: '2px',
          background: this.colors.accent,
          width: '100%',
          marginLeft: '-8px',
          paddingRight: '16px',
        },
      },
    ] as Styles[];
  }
}
